import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TablerIcon } from "@/components/icons/TablerIcon";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";
import { BuilderWordmark } from "@/components/BuilderWordmark";

function userInitials(email: string | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export function displayFirstName(email: string | undefined): string {
  if (!email) return "użytkowniku";
  const local = email.split("@")[0] ?? "";
  const part = local.split(/[._-]+/)[0] ?? local;
  if (!part) return "użytkowniku";
  return part.charAt(0).toUpperCase() + part.slice(1);
}

type NavItem =
  | { type: "link"; href: string; label: string; icon: Parameters<typeof TablerIcon>[0]["name"]; end?: boolean }
  | { type: "locked"; label: string; icon: Parameters<typeof TablerIcon>[0]["name"] };

const NAV_ITEMS: NavItem[] = [
  { type: "link", href: "/dashboard", label: "Start", icon: "home", end: true },
  { type: "link", href: "/dashboard/oferty", label: "Oferty", icon: "file-text", end: true },
  { type: "locked", label: "Umowy", icon: "file-contract" },
  { type: "locked", label: "Harmonogramy", icon: "calendar" },
  { type: "locked", label: "Koszty", icon: "coins" },
];

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/login");
    } finally {
      setSigningOut(false);
    }
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);

  const isActive = (href: string, end?: boolean) => {
    if (end) return location.pathname === href || location.pathname === `${href}/`;
    return location.pathname.startsWith(href);
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="dashboard-app workshop-modern">
      {sidebarOpen && (
        <button
          type="button"
          className="dashboard-sidebar-mask"
          aria-label="Zamknij menu"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={cn("dashboard-sidebar", sidebarOpen && "is-open")}
        aria-label="Nawigacja"
      >
        <Link to="/dashboard" className="dashboard-logo" onClick={closeSidebar}>
          <BuilderWordmark theme="dark" />
        </Link>

        <nav className="dashboard-nav">
          {NAV_ITEMS.map((item) => {
            if (item.type === "locked") {
              return (
                <span key={item.label} className="dashboard-nav-item is-locked" title="Wkrótce">
                  <TablerIcon name={item.icon} size={18} />
                  {item.label}
                  <TablerIcon name="lock" size={14} className="ml-auto opacity-50" />
                </span>
              );
            }
            const active = isActive(item.href, item.end);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn("dashboard-nav-item", active && "is-active")}
                onClick={closeSidebar}
              >
                <TablerIcon name={item.icon} size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="dashboard-sidebar-footer">
          <Link
            to="/ustawienia"
            className={cn(
              "dashboard-nav-item",
              location.pathname === "/ustawienia" && "is-active",
            )}
            onClick={closeSidebar}
          >
            <TablerIcon name="settings" size={18} />
            Ustawienia
          </Link>
          <button
            type="button"
            className="dashboard-nav-item"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
          >
            <TablerIcon name="logout" size={18} />
            {signingOut ? "Wylogowywanie…" : "Wyloguj"}
          </button>
          <div className="dashboard-user">
            <span className="dashboard-user-avatar" aria-hidden>
              {userInitials(user?.email)}
            </span>
            <span className="dashboard-user-email">{user?.email ?? "Konto"}</span>
          </div>
        </div>
      </aside>

      <div className="dashboard-shell">
        <header className="dashboard-topbar">
          <button
            type="button"
            className="dashboard-menu-btn"
            aria-label="Otwórz menu"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <TablerIcon name="menu-2" size={20} />
          </button>
          <div className="dashboard-topbar-spacer" />
          <button
            type="button"
            onClick={toggleTheme}
            className="dashboard-theme-toggle"
            aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
            title={theme === "dark" ? "Jasny motyw" : "Ciemny motyw"}
          >
            <TablerIcon name={theme === "dark" ? "sun" : "moon"} size={16} />
          </button>
        </header>

        <main className="dashboard-main">{children}</main>
      </div>
    </div>
  );
}
